import { useMemo } from 'react';
import I18n from 'I18n';
import { addUrlParams, getPublicAchievementUrl } from '../utils/RoutingUtils';
import { getAchievementUrlSlug } from '../utils/urls';
import { SOCIAL_LINKS, ACADEMY_DEFAULT_URL, ACADEMY_LINKEDIN_ORG_ID } from '../constants/CourseConstants';
import { useFullChirpTrack } from '../features/tracks';
import { useAchievementByTrackId } from '../features/achievement';
import { useUserInfo } from '../features/accounts/accountsQueries';
import { useTrackProgress } from '../features/userProgress';
import { useExamAttempts } from '../features/exams';
export const enrichTemplate = (template, grade) => {
  const gradePlaceHolder = /\{grade\}/g;
  template = template.replace(gradePlaceHolder, `${grade}`);
  return template;
};
export const getFacebookLink = (track, score, urlAchievement) => {
  const fbTemplate = track.facebookTemplate;
  const link = urlAchievement || track.facebookLink;
  if (!fbTemplate && !link) {
    return encodeURI(SOCIAL_LINKS.facebook_share);
  }
  const formattedTemplate = enrichTemplate(fbTemplate || '', score);
  const queryParams = `u=${link}&quote=${formattedTemplate}`;
  return encodeURI(`${SOCIAL_LINKS.facebook_share}?${queryParams}`);
};
export const getXLinkPreAchievement = (track, score) => {
  const xTemplate = track.twitterTemplate;
  if (!xTemplate) {
    return encodeURI(SOCIAL_LINKS.x_share);
  }
  const formattedTemplate = enrichTemplate(xTemplate, score);
  return encodeURI(`${SOCIAL_LINKS.x_share}?text=${formattedTemplate}`);
};
export const getXLink = (track, score, urlAchievement) => {
  const fbTemplate = track.facebookTemplate;
  const link = urlAchievement || track.facebookLink;
  if (!fbTemplate && !link) {
    return encodeURI(SOCIAL_LINKS.x_share);
  }
  const formattedTemplate = enrichTemplate(fbTemplate || '', score);
  return encodeURI(`${SOCIAL_LINKS.x_share}?text=${formattedTemplate} ${link}`);
};
export const getLinkedinLink = (track, urlAchievement) => {
  const link = urlAchievement || track.facebookLink || ACADEMY_DEFAULT_URL;
  return encodeURI(`${SOCIAL_LINKS.linkedin_share}?url=${link}`);
};

/**
 * Prepopulate fields in linkedin "licences and certifications"
 * according to: https://addtoprofile.linkedin.com/
 */
export const getLinkedInLicenseLink = (track, award, urlAchievement) => {
  if (track && award && urlAchievement && track.name && award.awardedAt && award.code) {
    const awardedAt = I18n.moment.userTz(award.awardedAt);
    const expiresAt = award.expiresAt && I18n.moment.userTz(award.expiresAt);
    return addUrlParams(SOCIAL_LINKS.linkedin_add_to_profile, {
      name: track.name,
      organizationId: ACADEMY_LINKEDIN_ORG_ID,
      issueYear: awardedAt.format('Y'),
      issueMonth: awardedAt.format('M'),
      expirationYear: expiresAt ? expiresAt.format('Y') : false,
      expirationMonth: expiresAt ? expiresAt.format('M') : false,
      certUrl: urlAchievement,
      certId: award.code
    });
  } else {
    return SOCIAL_LINKS.linkedin_add_to_profile;
  }
};
const useAwardSocialLinks = (trackId, language) => {
  const {
    achievement,
    achievementsLoading,
    achievementsError
  } = useAchievementByTrackId(trackId);
  const {
    publicId
  } = achievement || {};
  const {
    userInfo
  } = useUserInfo();
  const {
    trackProgress
  } = useTrackProgress(trackId);
  const award = trackProgress === null || trackProgress === void 0 ? void 0 : trackProgress.award;
  const {
    examAttempts
  } = useExamAttempts(trackId);
  const {
    track
  } = useFullChirpTrack(trackId);
  const examAttempt = award && examAttempts && award.examAttemptId ? examAttempts[award.examAttemptId] : null;
  const urlSlug = userInfo ? getAchievementUrlSlug({
    firstName: userInfo.user.firstName,
    lastName: userInfo.user.lastName,
    trackName: track === null || track === void 0 ? void 0 : track.name
  }) : '';
  const urlAchievement = !achievementsLoading && !achievementsError ? window.location.href.replace(/\/academy\/\d+\/.*$/, getPublicAchievementUrl(publicId, language, urlSlug)) : '';
  return useMemo(() => track && award && examAttempt ? {
    facebook: getFacebookLink(track, examAttempt.score, urlAchievement),
    x: urlAchievement ? getXLink(track, examAttempt.score, urlAchievement) : getXLinkPreAchievement(track, examAttempt.score),
    linkedin: getLinkedinLink(track, urlAchievement),
    linkedinAddToProfile: getLinkedInLicenseLink(track, award, urlAchievement),
    achievementUrl: urlAchievement
  } : null, [track, examAttempt, award, urlAchievement]);
};
export default useAwardSocialLinks;